import PropTypes from 'prop-types'
import { useEffect, useMemo, useCallback } from 'react'
import ImagesMap from './images-map'
import ImageLayer from '../../../ol/image-layer'
import Projection from 'ol/proj/Projection'
import * as olExtent from 'ol/extent'
import View from 'ol/View'
import useImage from '../../../../hooks/use-image'
import Message from '../../elements/message'
import NavButton from './nav-button'
import { getImageMetaData } from '../../../../utils/image-utils'
import { maxImageZoom } from '../../../../ol-styles'

export default function ImageView({
  apiClient,
  projectId,
  images,
  setSelectedImage,
  selectedImage,
  map,
  onMapInitialized,
  info,
  setImageStatus,
  navigationDisabled = false,
  units = 'metric',
}) {
  useEffect(() => {
    if (images) {
      setSelectedImage(images.features[0])
    }
  }, [images, setSelectedImage])

  const selectedImageIndex = images && getSelectedImageIndex()

  const onNextImage = () => {
    const nextIndex = (selectedImageIndex + 1) % images.features.length
    setSelectedImage(images.features[nextIndex])
  }

  const onPreviousImage = () => {
    const previousIndex =
      (selectedImageIndex + images.features.length - 1) % images.features.length

    setSelectedImage(images.features[previousIndex])
  }

  const getImageUrl = useCallback(
    image => apiClient.getProjectPhotoUrl(projectId, image.properties.filename),
    [apiClient, projectId]
  )

  const { loadedImage, extent, status, error } = useImage(
    selectedImage,
    getImageUrl
  )

  const projection = useMemo(
    () =>
      new Projection(
        {
          code: 'drone-image',
          units: 'pixels',
          extent: extent,
        },
        [extent]
      ),
    [extent]
  )

  useEffect(() => {
    if (map && extent && loadedImage) {
      const view = new View({
        projection: projection,
        center: olExtent.getCenter(extent),
        extent: olExtent.buffer(
          extent,
          Math.max(loadedImage.width, loadedImage.height) * 0.05
        ),
        zoom: 2,
        minZoom: 2,
        maxZoom: maxImageZoom,
      })
      map.setView(view)
    }
  }, [extent, loadedImage, projection, map])

  useEffect(() => {
    setImageStatus && setImageStatus(status)
  }, [setImageStatus, status])

  const imageMetaData = useMemo(
    () => selectedImage && getImageMetaData(selectedImage, units),
    [selectedImage, units]
  )

  return (
    <ImageMapContainer>
      <NavButton
        icon="chevron-left"
        disabled={navigationDisabled}
        onClick={onPreviousImage}
        className="tw-absolute tw-left-0 tw-ml-9"
      />
      <div className="tw-w-full tw-h-full">
        {selectedImage ? (
          <ImagesMap
            onMapInitialized={onMapInitialized}
            filename={selectedImage.properties?.filename}
            indexInfo={`${selectedImageIndex + 1} / ${
              images?.features?.length
            }`}
            metadata={imageMetaData}
            info={info}
            status={status}
            error={error}
          />
        ) : (
          <Message message="No image selected" />
        )}
        {map && loadedImage && extent && (
          <ImageLayer
            map={map}
            imageUrl={loadedImage.src}
            projection={projection}
            extent={extent}
          />
        )}
      </div>
      <NavButton
        icon="chevron-right"
        className="tw-absolute tw-right-0 tw-mr-9"
        disabled={navigationDisabled}
        onClick={onNextImage}
      />
    </ImageMapContainer>
  )

  function getSelectedImageIndex() {
    return images?.features && selectedImage?.properties
      ? images.features.findIndex(
          feature =>
            feature.properties.filename === selectedImage.properties.filename
        )
      : -1
  }
}

ImageView.propTypes = {
  apiClient: PropTypes.shape({
    getProjectPhotoUrl: PropTypes.func,
  }),
  images: PropTypes.shape({
    features: PropTypes.array,
  }),
  map: PropTypes.shape({
    setView: PropTypes.func,
  }),
  navigationDisabled: PropTypes.bool,
  onMapInitialized: PropTypes.func,
  projectId: PropTypes.number,
  selectedImage: PropTypes.shape({
    properties: PropTypes.shape({
      filename: PropTypes.string,
    }),
  }),
  setSelectedImage: PropTypes.func,
  info: PropTypes.node,
  units: PropTypes.string,
  setImageStatus: PropTypes.func,
}

export function ImageMapContainer({ children }) {
  return (
    <div className="tw-flex tw-justify-between tw-w-full tw-h-full tw-flex-grow-1 tw-items-center tw-p-4 tw-max-h-image-map tw-relative">
      {children}
    </div>
  )
}

ImageMapContainer.propTypes = { children: PropTypes.element }
